import React from "react";

import { Footer2026BodyBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Cta } from "../shared/Cta";
import { LogoMark } from "../shared/LogoMark";
import { PageGlow } from "../shared/glow";

import styles from "./index.module.scss";

interface Props {
    value: Footer2026BodyBlock;
}

// Four columns have five edges; at two columns the extra lines are hidden
const LINES = [0, 1, 2, 3, 4];

/** `+1.212.209.1333` → `tel:+12122091333` */
// The generated type allows null, though the field is required
const telHref = (phone: string | null) => {
    const trimmed = (phone ?? "").trim();
    const digits = trimmed.replace(/\D/g, "");
    return `tel:${trimmed.startsWith("+") ? "+" : ""}${digits}`;
};

/**
 * The nine-dot mark. It turns in and grows to size whenever it comes on
 * screen, and resets when it leaves in either direction, so it plays again.
 * The observed wrapper is never transformed, so the test can't feed back on
 * itself.
 */
const FooterMark = () => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [shown, setShown] = React.useState(false);

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) =>
            entries.forEach((e) => setShown(e.isIntersecting)),
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);

    return (
        <div ref={ref} className={styles.mark} aria-hidden="true">
            <LogoMark
                className={concatClassNames([
                    styles.markSvg,
                    shown ? styles.markIn : undefined,
                ])}
            />
        </div>
    );
};

/** The 2026 site footer. Offices come from the 2026 footer setting. */
export const Footer2026 = (props: Props) => {
    const year = new Date().getFullYear();
    return (
        <footer className={styles.root}>
            <PageGlow variant="footer" className={styles.glow} />
            <div className={styles.grid} aria-hidden="true">
                {LINES.map((line) => (
                    <i
                        key={line}
                        className={styles.line}
                        style={{ "--line": line }}
                    />
                ))}
            </div>

            <div className={styles.band}>
                <FooterMark />
                <h2 className={styles.head}>Tell us what you want to build.</h2>
                <Cta
                    variant="outlineOnDark"
                    opensContactModal
                    className={styles.cta}
                >
                    Start a conversation
                </Cta>
            </div>

            {props.value.length > 0 && (
                <div className={styles.locations}>
                    {props.value.map(({ id, value }) => (
                        <div key={id} className={styles.office}>
                            <p className={styles.city}>{value.city}</p>
                            <p className={styles.address}>{value.address}</p>
                            <p className={styles.phone}>
                                <a href={telHref(value.phone)}>{value.phone}</a>
                            </p>
                        </div>
                    ))}
                </div>
            )}

            <div className={styles.legal}>
                <div className={styles.end}>
                    <span>
                        ©{year} thelab. Part of{" "}
                        <a
                            href="https://www.wellcomww.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            the wellcom group
                        </a>
                    </span>
                    <nav className={styles.legalNav} aria-label="Legal">
                        <a href="/privacy-policy">Privacy</a>
                        <a href="/modern-slavery-statement">
                            Modern Slavery Statement
                        </a>
                        <a href="/whistleblower-policy">
                            Whistleblower Protection
                        </a>
                    </nav>
                    <div className={styles.social}>
                        <a
                            className={styles.icon}
                            href="https://www.instagram.com/thelabdotco/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="thelab on Instagram"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                            >
                                <rect
                                    x="4"
                                    y="4"
                                    width="16"
                                    height="16"
                                    rx="4.6"
                                />
                                <circle cx="12" cy="12" r="3.6" />
                                <circle
                                    cx="17"
                                    cy="7"
                                    r=".9"
                                    fill="currentColor"
                                    stroke="none"
                                />
                            </svg>
                        </a>
                        <a
                            className={styles.icon}
                            href="https://www.linkedin.com/company/thelab"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="thelab on LinkedIn"
                        >
                            <svg
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                fill="currentColor"
                            >
                                <rect x="5.2" y="9.6" width="2.7" height="9" />
                                <circle cx="6.55" cy="6.4" r="1.6" />
                                <path d="M10.1 9.6h2.6v1.25a2.9 2.9 0 0 1 2.6-1.4c2 0 3.2 1.25 3.2 3.75v5.4h-2.7v-4.9c0-1.25-.45-2-1.55-2-.95 0-1.45.63-1.45 2v4.9h-2.7z" />
                            </svg>
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};
